import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Subscription, ESubscriptionStatus, ESubType, subscriptionService, getSubscriptionStreamingAccount, ESubscriptionType, EownerType } from '@/services/subscription';
import { ExtendSubscriptionModal } from '@/components/subscriptions/ExtendSubscriptionModal';
import { StreamingServiceCombobox } from '@/components/shared/StreamingServiceCombobox';
import { ServiceBadge } from './ServiceBadge';
import { StatusBadge } from './StatusBadge';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, Clock, Eye, Loader2 } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

function mapStatus(status: ESubscriptionStatus): 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'CANCELLED' {
  const statusMap: Record<ESubscriptionStatus, 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'CANCELLED'> = {
    [ESubscriptionStatus.ACTIVE]: 'ACTIVE',
    [ESubscriptionStatus.EXPIRING]: 'EXPIRING',
    [ESubscriptionStatus.EXPIRED]: 'EXPIRED',
    [ESubscriptionStatus.CANCELLED]: 'CANCELLED',
  };
  return statusMap[status] || 'ACTIVE';
}

const PAGE_SIZE = 10;

export function PaymentsTable() {
  const navigate = useNavigate();
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['subscriptions', 'payments-table', serviceFilter],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await subscriptionService.getAll({
        ownerType:EownerType.CLIENT,
        page: pageParam as number,
        limit: PAGE_SIZE,
        status: ESubscriptionStatus.ACTIVE,
        serviceId: serviceFilter !== 'all' ? Number(serviceFilter) : undefined,
      });

      // El backend responde 204 (sin body) cuando no hay resultados,
      // por lo que `response` puede llegar vacío/sin `data` o `pagination`.
      if (!response?.data) {
        return {
          ...response,
          data: [],
          pagination: {
            totalPages: (pageParam as number) - 1,
            totalItems: 0,
            pageIndex: pageParam as number,
            pageSize: PAGE_SIZE,
          },
        };
      }

      return response;
    },
    getNextPageParam: (lastPage) => {
      const { pageIndex, totalPages } = lastPage.pagination ?? {};
      if (pageIndex === undefined || totalPages === undefined) return undefined;
      return pageIndex < totalPages ? pageIndex + 1 : undefined;
    },
    initialPageParam: 1,
  });

  // IntersectionObserver para scroll infinito
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allSubscriptions = data?.pages.flatMap((page) => page.data ?? []) ?? [];

  const sortedSubscriptions = [...allSubscriptions].sort((a, b) => {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    return (a.daysRemaining - b.daysRemaining) * multiplier;
  });

  const getRowClass = (subscription: Subscription) => {
    if (subscription.status === ESubscriptionStatus.EXPIRED) return 'table-row-urgent';
    if (subscription.daysRemaining <= 2) return 'table-row-warning';
    return '';
  };

  if (isLoading) {
    return (
      <div className="card-elevated overflow-hidden">
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Cargando suscripciones...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card-elevated overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 lg:p-5 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Accounts to Charge Soon</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manage upcoming renewals and payments</p>
        </div>
        <div className="flex items-center gap-3">
          <StreamingServiceCombobox
            value={serviceFilter}
            onChange={setServiceFilter}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="gap-2"
          >
            <ArrowUpDown className="h-4 w-4" />
            {sortOrder === 'asc' ? 'Soonest' : 'Latest'}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-muted/90 backdrop-blur-sm">
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">Client</TableHead>
              <TableHead className="font-semibold">Service</TableHead>
              <TableHead className="font-semibold hidden md:table-cell">Account</TableHead>
              <TableHead className="font-semibold hidden lg:table-cell">Slot</TableHead>
              <TableHead className="font-semibold">End Date</TableHead>
              <TableHead className="font-semibold text-center">Days Left</TableHead>
              <TableHead className="font-semibold text-right">Amount</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedSubscriptions.map((subscription) => {
              const item = subscription.subscriptionItems?.[0];
              const service = item?.service;
              return (
                <TableRow key={subscription.id} className={cn("transition-colors", getRowClass(subscription))}>
                  <TableCell className="font-medium text-foreground">{subscription?.client.name}</TableCell>
                  <TableCell>
                    {subscription.type === ESubType.COMBO ? (
                      <ServiceBadge icon="📦" name={subscription?.combo?.name || "Combo"} />
                    ) : (
                      <ServiceBadge
                        icon={service?.icon || ""}
                        name={service?.name || ""}
                      />
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {subscription.type === ESubType.COMBO
                      ? "Múltiples cuentas"
                      : getSubscriptionStreamingAccount(subscription)?.email || ''}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {subscription.type === ESubType.SINGLE
                      ? item?.subscriptionType === ESubscriptionType.SLOT
                        ? item?.slot?.name
                        : "Cuenta completa"
                      : 'Combo'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(subscription.endDate).toLocaleDateString('es-CO', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={cn(
                      "inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                      subscription.daysRemaining <= 0 && "bg-destructive text-destructive-foreground",
                      subscription.daysRemaining <= 2 && subscription.daysRemaining > 0 && "bg-warning text-warning-foreground",
                      subscription.daysRemaining > 2 && "bg-muted text-muted-foreground"
                    )}>
                      {subscription.daysRemaining}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-foreground">
                    $ {formatCurrency(subscription.price)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={mapStatus(subscription.status)} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setSelectedSubscription(subscription);
                              setIsExtendModalOpen(true);
                            }}
                          >
                            <Clock className="h-4 w-4 text-primary" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Extend</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => navigate(`/subscriptions/${subscription.id}`)}
                          >
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>View</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/* Sentinel para scroll infinito */}
        <div ref={sentinelRef} className="py-2 flex items-center justify-center">
          {isFetchingNextPage && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
        </div>
      </div>

      {sortedSubscriptions.length === 0 && (
        <div className="p-8 text-center">
          <p className="text-muted-foreground">No upcoming payments found</p>
        </div>
      )}

      <ExtendSubscriptionModal
        open={isExtendModalOpen}
        onOpenChange={setIsExtendModalOpen}
        subscription={selectedSubscription}
      />
    </div>
  );
}
